import {rowLabel} from "./trialBalanceLabels.ts";
import {WARNING_TAG_TEXT, warnedRowAncestorIds} from "./rowWarnings.ts";
import {ReportRowKind, type ReportRow} from "../types/types.ts";
import type {LegalEntityType} from "../../../../generated/enums.ts";

export type ProjectableRow = Pick<
    ReportRow,
    "id" | "kind" | "parent_id" | "label_key" | "account" | "warning" | "subledger" | "subledger_split"
>;

type Projected<T> = T & {expanded: boolean};

function isExpandable(row: ProjectableRow): boolean {
    return row.kind === ReportRowKind.Group;
}

export function defaultOpenNodes(rows: ProjectableRow[]): Record<string, boolean> {
    const groupIds = new Set(
        rows.filter((row) => row.kind === ReportRowKind.Group).map((row) => row.id),
    );
    const hasSubgroup = new Set(
        rows
            .filter(
                (row) =>
                    row.kind === ReportRowKind.Group &&
                    row.parent_id !== null &&
                    groupIds.has(row.parent_id),
            )
            .map((row) => row.parent_id as string),
    );
    const revealsWarning = new Set(warnedRowAncestorIds(rows));
    const open: Record<string, boolean> = {};
    for (const row of rows) {
        if (!isExpandable(row)) continue;
        if (hasSubgroup.has(row.id) || revealsWarning.has(row.id)) open[row.id] = true;
    }
    return open;
}

export function allOpenNodes(rows: ProjectableRow[]): Record<string, boolean> {
    const open: Record<string, boolean> = {};
    for (const row of rows) {
        if (isExpandable(row)) open[row.id] = true;
    }
    return open;
}

export function allClosedNodes(rows: ProjectableRow[]): Record<string, boolean> {
    const closed: Record<string, boolean> = {};
    for (const row of rows) {
        if (isExpandable(row)) closed[row.id] = false;
    }
    return closed;
}

export function resolveOpenNodes(
    rows: ProjectableRow[],
    overrides: Record<string, boolean>,
): Record<string, boolean> {
    const defaults = defaultOpenNodes(rows);
    const resolved: Record<string, boolean> = {};
    for (const row of rows) {
        if (isExpandable(row)) {
            resolved[row.id] = row.id in overrides ? overrides[row.id] : !!defaults[row.id];
        }
    }
    return resolved;
}

export function isBeyondDefault(
    openNodes: Record<string, boolean>,
    rows: ProjectableRow[],
): boolean {
    const baseline = defaultOpenNodes(rows);
    return Object.entries(openNodes).some(([id, isOpen]) => isOpen && !baseline[id]);
}

function searchText(row: ProjectableRow, legalEntityType: LegalEntityType): string {
    const code = row.account?.code ?? "";
    const warningTag = row.warning ? WARNING_TAG_TEXT : "";
    return `${code} ${rowLabel(row, legalEntityType)} ${warningTag}`.toLowerCase();
}

function withExpanded<T extends ProjectableRow>(row: T, openNodes: Record<string, boolean>): Projected<T> {
    return {...row, expanded: !!openNodes[row.id]};
}

function projectCollapse<T extends ProjectableRow>(
    rows: T[],
    openNodes: Record<string, boolean>,
): Projected<T>[] {
    const byId = new Map(rows.map((row) => [row.id, row]));

    function hiddenByCollapsedAncestor(row: T): boolean {
        let parentId = row.parent_id;
        while (parentId) {
            const parent = byId.get(parentId);
            if (!parent) break;
            if (isExpandable(parent) && !openNodes[parent.id]) return true;
            parentId = parent.parent_id;
        }
        return false;
    }

    return rows
        .filter((row) => !hiddenByCollapsedAncestor(row))
        .map((row) => withExpanded(row, openNodes));
}

function projectSearch<T extends ProjectableRow>(
    rows: T[],
    query: string,
    legalEntityType: LegalEntityType,
): Projected<T>[] {
    const byId = new Map(rows.map((row) => [row.id, row]));
    const matchedGroupIds = new Set(
        rows
            .filter(
                (row) =>
                    row.kind === ReportRowKind.Group &&
                    searchText(row, legalEntityType).includes(query),
            )
            .map((row) => row.id),
    );
    const kept = new Set<string>();

    const keepWithAncestors = (row: T): void => {
        kept.add(row.id);
        let parentId = row.parent_id;
        while (parentId) {
            const parent = byId.get(parentId);
            if (!parent) break;
            kept.add(parent.id);
            parentId = parent.parent_id;
        }
    };

    const hasMatchedGroupAncestor = (row: T): boolean => {
        let parentId = row.parent_id;
        while (parentId) {
            if (matchedGroupIds.has(parentId)) return true;
            parentId = byId.get(parentId)?.parent_id ?? null;
        }
        return false;
    };

    for (const row of rows) {
        if (row.kind === ReportRowKind.Subtotal) continue;
        if (matchedGroupIds.has(row.id)) {
            keepWithAncestors(row);
            continue;
        }
        if (hasMatchedGroupAncestor(row)) {
            keepWithAncestors(row);
            continue;
        }
        if (searchText(row, legalEntityType).includes(query)) {
            keepWithAncestors(row);
        }
    }

    for (const row of rows) {
        if (row.kind !== ReportRowKind.Subtotal) continue;
        if (row.parent_id && kept.has(row.parent_id)) kept.add(row.id);
    }

    return rows.filter((row) => kept.has(row.id)).map((row) => ({...row, expanded: true}));
}

export function projectRows<T extends ProjectableRow>(input: {
    rows: T[];
    openNodes: Record<string, boolean>;
    searchQuery: string;
    legalEntityType: LegalEntityType;
}): Projected<T>[] {
    const query = input.searchQuery.trim().toLowerCase();
    if (query) return projectSearch(input.rows, query, input.legalEntityType);
    return projectCollapse(input.rows, input.openNodes);
}
